import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { ChevronDown } from 'lucide-react';
import clsx from 'clsx';
import { formatDisplayDate, formatShipDate } from '../../utils/dates';
import { useFormStore } from '../../stores/formData';
import useOrderApi from '../../hooks/useOrderApi';
import { formatPrice } from '../../utils/quantities';

const AlternatePickupDateSelector = ({
  legIndex,
  onChange,
  isDark = false,
  className = ''
}) => {
  const { getAvailableDates } = useOrderApi();
  
  // Get all leg data from store
  const currentLeg = useFormStore((state) => state.formData.legs?.[legIndex]);
  const shipDate = currentLeg?.selectedService?.ship_date;
  const alternateShipDate = currentLeg?.alternateShipDate;
  const fromAddress = currentLeg?.fromAddress;
  const toAddress = currentLeg?.toAddress;
  const serviceName = currentLeg?.selectedService?.display_name;
  const price = currentLeg?.selectedService?.combined_adjusted_price_cents || 
                currentLeg?.selectedService?.price_cents;
  const transitDays = currentLeg?.selectedService?.transit_days || 0;
  const deliveryDate = currentLeg?.embarkDate || currentLeg?.deliveryDate;

  // State for date constraints from API
  const [dateConstraints, setDateConstraints] = useState(null);
  const [loadingDates, setLoadingDates] = useState(false);

  // Auto-expand if alternateShipDate is already set
  const [isExpanded, setIsExpanded] = useState(!!alternateShipDate);

  // Update expanded state if alternateShipDate changes externally
  useEffect(() => {
    if (alternateShipDate) {
      setIsExpanded(true);
    }
  }, [alternateShipDate]);

  // Fetch date constraints when addresses and service are available
  useEffect(() => {
    if (!fromAddress || !toAddress) return;

    const fetchDateConstraints = async () => {
      setLoadingDates(true);
      try {
        const selectedService = currentLeg?.selectedService;
        const details = selectedService ? {
          carrier_service_level_id: selectedService.carrier_service_level_id,
          ship_date: selectedService.ship_date,
          transit_days: selectedService.transit_days
        } : {};

        const constraints = await getAvailableDates(fromAddress, toAddress, details);
        setDateConstraints(constraints);
      } catch (error) {
        console.error('Error fetching date constraints:', error);
      } finally {
        setLoadingDates(false);
      }
    };

    fetchDateConstraints();
  }, [fromAddress, toAddress, currentLeg?.selectedService, getAvailableDates]);

  // Check if a specific date is blocked (for delivery/transit calculation)
  const isDateBlocked = useCallback((date) => {
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    
    const dayOfWeek = checkDate.getDay();

    // Check blocked days of week (weekends: 0 = Sunday, 6 = Saturday)
    // If no constraints, default to blocking weekends
    if (!dateConstraints) {
      return dayOfWeek === 0 || dayOfWeek === 6;
    }

    if (dateConstraints.days?.includes(dayOfWeek)) {
      return true;
    }

    // Check specific blocked dates
    const dateStr = formatShipDate(checkDate, 'MM/DD/YYYY');
    if (dateConstraints.dates?.[dateStr]) {
      return true;
    }

    // Check permanent holidays [month, day] format
    const month = checkDate.getMonth() + 1;
    const day = checkDate.getDate();
    if (dateConstraints.permanent?.some(([m, d]) => Number(m) === month && Number(d) === day)) {
      return true;
    }

    return false;
  }, [dateConstraints]);

  // Calculate estimated arrival date accounting for blocked days (weekends, holidays)
  const calculateArrivalDate = useCallback((fromShipDate, days) => {
    const arrival = new Date(fromShipDate);
    arrival.setHours(0, 0, 0, 0);
    let daysToAdd = days;

    while (daysToAdd > 0) {
      arrival.setDate(arrival.getDate() + 1);
      // Only count non-blocked days (business days) toward transit
      if (!isDateBlocked(arrival)) {
        daysToAdd--;
      }
    }

    // If arrival lands on a blocked day, move to next valid day
    while (isDateBlocked(arrival)) {
      arrival.setDate(arrival.getDate() + 1);
    }

    return arrival;
  }, [isDateBlocked]);

  // Check if a ship date arrives on time (at least 1 day before delivery date)
  // skip check if the service has not been selected yet
  const arrivesOnTime = useCallback((shipDateToCheck) => {
    if (!deliveryDate || !transitDays) return true; // service not selected yet

    const deliveryDeadline = new Date(deliveryDate);
    deliveryDeadline.setHours(0, 0, 0, 0);

    const estimatedArrival = calculateArrivalDate(shipDateToCheck, transitDays);
    estimatedArrival.setHours(0, 0, 0, 0);

    // Must arrive at least 1 day before delivery date
    const oneDayBefore = new Date(deliveryDeadline);
    oneDayBefore.setDate(oneDayBefore.getDate() - 1);

    return estimatedArrival <= oneDayBefore;
  }, [deliveryDate, transitDays, calculateArrivalDate]);

  // Check if a date is valid for pickup
  const isDateValidForPickup = useCallback((date) => {
    if (!date) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (date < today) return false;

    // Fall back to basic weekend check if no constraints loaded
    if (!dateConstraints) {
      const dayOfWeek = date.getDay();
      return dayOfWeek !== 0 && dayOfWeek !== 6;
    }

    const dayOfWeek = date.getDay();

    if (dateConstraints.days?.includes(dayOfWeek)) {
      return false;
    }

    const dateStr = formatShipDate(date, 'MM/DD/YYYY');
    if (dateConstraints.dates?.[dateStr]?.pickup === false) {
      return false;
    }

    const month = date.getMonth() + 1;
    const day = date.getDate();
    if (dateConstraints.permanent?.some(([m, d]) => m === month && d === day)) {
      return false;
    }

    return true;
  }, [dateConstraints]);

  // Combined validation: valid for pickup AND arrives on time
  const isDateValid = useCallback((date) => {
    return isDateValidForPickup(date) && arrivesOnTime(date);
  }, [isDateValidForPickup, arrivesOnTime]);

  // Calculate alternate date options - always try to return 3 dates
  const dateOptions = useMemo(() => {
    if (!shipDate) return [];

    // Wait for constraints to load before calculating options
    if (loadingDates) return [];

    const baseDate = new Date(shipDate);
    const options = [];
    const maxSearchDays = 14;

    // Find previous valid dates (search backwards)
    let prevDate = new Date(baseDate);
    for (let i = 1; i <= maxSearchDays && options.length < 1; i++) {
      prevDate.setDate(prevDate.getDate() - 1);
      if (isDateValid(prevDate)) {
        options.push({
          date: new Date(prevDate),
          dateString: formatShipDate(prevDate),
          displayDate: formatDisplayDate(prevDate),
          estimatedArrival: calculateArrivalDate(prevDate, transitDays),
          isRecommended: false
        });
      }
    }

    // Add recommended date (original ship date) - only if it arrives on time
    if (arrivesOnTime(baseDate)) {
      options.push({
        date: baseDate,
        dateString: formatShipDate(baseDate),
        displayDate: formatDisplayDate(baseDate),
        estimatedArrival: calculateArrivalDate(baseDate, transitDays),
        isRecommended: true
      });
    }

    // Find next valid dates (search forwards) - fill remaining slots up to 3 total
    let nextDate = new Date(baseDate);
    for (let i = 1; i <= maxSearchDays && options.length < 3; i++) {
      nextDate.setDate(nextDate.getDate() + 1);
      if (isDateValid(nextDate)) {
        options.push({
          date: new Date(nextDate),
          dateString: formatShipDate(nextDate),
          displayDate: formatDisplayDate(nextDate),
          estimatedArrival: calculateArrivalDate(nextDate, transitDays),
          isRecommended: false
        });
      }
    }

    // If we still don't have 3, search further back
    if (options.length < 3) {
      const earliestDate = options.length > 0 
        ? new Date(Math.min(...options.map(o => o.date.getTime())))
        : new Date(baseDate);
      
      let searchDate = new Date(earliestDate);
      for (let i = 1; i <= maxSearchDays && options.length < 3; i++) {
        searchDate.setDate(searchDate.getDate() - 1);
        const dateStr = formatShipDate(searchDate);
        if (isDateValid(searchDate) && !options.some(o => o.dateString === dateStr)) {
          options.unshift({
            date: new Date(searchDate),
            dateString: dateStr,
            displayDate: formatDisplayDate(searchDate),
            estimatedArrival: calculateArrivalDate(searchDate, transitDays),
            isRecommended: false
          });
        }
      }
    }

    // Sort by date ascending
    options.sort((a, b) => a.date.getTime() - b.date.getTime());

    return options;
  }, [shipDate, isDateValid, arrivesOnTime, calculateArrivalDate, transitDays, loadingDates, dateConstraints]);

  const selectedDateString = alternateShipDate 
    ? formatShipDate(alternateShipDate) 
    : null;

  const handleDateSelect = (option) => {
    onChange?.(option.date);
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // Styling
  const linkClassName = clsx(
    'flex items-center gap-1 text-sm font-medium cursor-pointer transition-colors mt-4 underline',
    {
      'text-blue-400 hover:text-blue-300': isDark,
      'text-gray-900 hover:text-gray-700': !isDark
    }
  );

  const optionCardClassName = (isSelected) => clsx(
    'w-full p-4 border rounded-lg text-left transition-all duration-200',
    {
      'border-slate-600 bg-slate-700/50 hover:bg-slate-700': isDark && !isSelected,
      'border-green-500 bg-green-500/10': isDark && isSelected,
      'border-gray-200 hover:border-gray-300 bg-white': !isDark && !isSelected,
      'border-green-500 bg-green-50': !isDark && isSelected
    }
  );

  const dateTextClassName = clsx(
    'font-medium',
    {
      'text-white': isDark,
      'text-gray-900': !isDark
    }
  );

  const serviceTextClassName = clsx(
    'text-sm',
    {
      'text-slate-400': isDark,
      'text-gray-600': !isDark
    }
  );

  const priceClassName = clsx(
    'font-semibold',
    {
      'text-white': isDark,
      'text-gray-900': !isDark
    }
  );

  // Don't render if no service selected
  if (!shipDate || !currentLeg?.selectedService) {
    return null;
  }

  return (
    <div className={className} data-testid="alternate-pickup-selector">
      <button
        type="button"
        onClick={toggleExpanded}
        className={linkClassName}
        aria-expanded={isExpanded}
      >
        <span>Need a different pick up date?</span>
        <ChevronDown
          size={16}
          className={clsx(
            'transition-transform duration-200',
            { 'rotate-180': isExpanded }
          )}
        />
      </button>

      {isExpanded && (
        <div className="mt-4 space-y-3">
          {loadingDates && (
            <div className="text-sm text-gray-500">Loading available dates...</div>
          )}
          {!loadingDates && dateOptions.length === 0 && (
            <div className="text-sm text-gray-500">
              No alternate dates available that meet the delivery deadline.
            </div>
          )}
          {dateOptions.map((option) => {
            const isSelected = option.dateString === selectedDateString;
            
            return (
              <button
                key={option.dateString}
                type="button"
                onClick={() => handleDateSelect(option)}
                className={optionCardClassName(isSelected)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center flex-1">
                    <input
                      type="radio"
                      checked={isSelected}
                      onChange={() => {}}
                      className="mr-3 w-4 h-4"
                      readOnly
                    />
                    <div className="flex-1">
                      <div className={dateTextClassName}>
                        {option.displayDate}
                      </div>
                      <div className={serviceTextClassName}>
                        {serviceName}
                      </div>
                      {/* <div className={arrivalTextClassName}>
                        Est. arrival: {formatDisplayDate(option.estimatedArrival)}
                      </div> */}
                    </div>
                  </div>
                  <div className={priceClassName}>
                    {formatPrice(price)}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

AlternatePickupDateSelector.propTypes = {
  legIndex: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  isDark: PropTypes.bool,
  className: PropTypes.string
};

export default AlternatePickupDateSelector;